import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faEdit } from '@fortawesome/free-solid-svg-icons'
import { PhoneNumberUtil } from 'google-libphonenumber'
import { useEffect, useState, useRef } from 'react'
import { useOhData } from '../layouts/OhLayout'
import { useNavigate } from 'react-router-dom'

import useAxiosPrivateWBO from '../hooks/useAxiosPrivateWBO'
import useAuth from '../hooks/useAuth'

import UserDetailsSection from './UserDetailsSection'
import CancelRequest from './CancelRequest'
import OHRequestForm from './OHRequestForm'

const OHRequestPage = () => {
    const { auth, setShowLoader, createInfo } = useAuth()
    const { orgInfo, requestData, setRequestData } = useOhData()

    const [loading, setLoading] = useState(true)

    const navigate = useNavigate()
    const axios = useAxiosPrivateWBO()

    const isDraftEdit = (requestData?.request_status || '').toLowerCase() === 'draft'
    const isEdit = requestData && (requestData?.request_status || '').toLowerCase() !== 'draft'

    const [editMode, setEditMode] = useState(false)

    const [saveDraftConfirmOpen, setSaveDraftConfirmOpen] = useState(false)
    const [showMoreBelowHint, setShowMoreBelowHint] = useState(false)
    const [showValidationTooltip, setShowValidationTooltip] = useState(false)
    const [workingHoursDropdown, setWorkingHoursDropdown] = useState('FT')

    const formRef = useRef(null)

    const [formData, setFormData] = useState({
        staff_details: {
            first_name: '',
            last_name: '',
            staff_name: '',
            staff_email: '',
            date_of_birth: '',
            phone_number: '',
            role: '',
            in_work: null,
            full_time: false,
            working_hours: '',
            contact_name: auth.user.contact_name,
            contact_email: auth.user.contact_email,
        },
        form_responses: {
            question1: {
                question_title: 'What support is being sought and why?',
                responses: []
            },
            question2: {
                question_title: 'What is the main nature of the employee issue?',
                responses: []
            },
            question3: {
                question_title: 'What has happened so far?',
                responses: []
            },
            question4: {
                question_title: 'What actions have been implemented?',
                responses: []
            },
            question5: {
                question_title: 'What is the desired outcome?',
                responses: []
            },
            question6: {
                question_title: 'What questions would you want us to ask the employee?',
                responses: []
            },
            question7: {
                question_title: 'Additional Information',
                responses: [
                    {
                        answer_text: "Additional Information",
                        responses: [],
                        text_response: ""
                    }
                ]
            }
        },
        declarations: {
            purpose_explained: false,
            questions_aware: false,
            abandonment: false
        },
        titleId: '13172b95-d9db-431d-957c-7f3049c24f6f',
        policyId: auth?.currentPolicy?.policy_id ?? '',
        school_name: auth.schools[0].name ?? '',
        school_address: auth.schools[0].address ?? '',
        school_postcode: auth.schools[0].postcode ?? '',
        org_id: auth.schools[0].id ?? '',
        org_urn: auth.schools[0].urn ?? '',
    })

    const [files, setFiles] = useState({
        fileList: [],
        uploading: false,
        errorMsg: false,
    })

    const [draftOnly, setDraftOnly] = useState(true)

    useEffect(() => {
        const masterOH = auth?.user?.master_oha

        if (masterOH) {
            setDraftOnly(false)
        }

        const policyInformation = JSON.parse(auth?.currentPolicy?.policy_information)
        const restrictNonMasterSubmissions = policyInformation?.settings?.general?.restrict_non_master_submissions

        if (!policyInformation) return

        if (restrictNonMasterSubmissions !== true) {
            setDraftOnly(false)
        }
    }, [auth?.currentPolicy?.policy_information])

    useEffect(() => {

        setLoading(true)
        try {
            if (requestData && requestData?.request_information) {
                const requestInfo = JSON.parse(requestData.request_information)

                if (!!requestInfo?.v2) {
                    setFormData(prevFormData => ({
                        ...prevFormData,
                        staff_details: requestInfo.staff_details,
                        form_responses: requestInfo.form_responses,
                        declarations: requestInfo.declarations,
                        titleId: requestInfo.titleId,
                    }))
                } else {
                    processLegacyRequestData(requestInfo)
                }
            }
        } catch (error) {
            console.error('Error initializing OH request page:', error)
        } finally {
            setLoading(false)
        }
    }, [requestData])

    const processLegacyRequestData = (requestInformation) => {

        let rawFormData = requestInformation?.raw_form_data

        if (!rawFormData) return false

        const question1 = rawFormData['question1'];
        const question2 = rawFormData['question2'];
        const question3 = rawFormData['question3'];
        const question4 = rawFormData['question4'];
        const question5 = rawFormData['question5'];
        const question6 = rawFormData['question6'];

        const migratedFormResponses = {
            question1: {
                question_title: 'What support is being sought and why?',
                responses: [
                    {
                        answer_text: question1?.supportReason,
                        sub_responses: [
                            {
                                answer_text: question1?.policyTriggerDetail,
                                sub_responses: []
                            }
                        ],
                    }
                ]
            },
            question2: {
                question_title: 'What is the main nature of the employee issue?',
                responses: [
                    {
                        answer_text: question2?.issueNature,
                        sub_responses: [
                            {
                                answer_text: question2?.functionalDetail,
                                sub_responses: []
                            }
                        ],
                    }
                ]
            },
            question3: {
                question_title: 'What has happened so far?',
                responses: [
                    {
                        answer_text: "Meetings Held",
                        sub_responses: [
                            {
                                answer_text: question3?.meetingsHeld,
                                sub_responses: []
                            }
                        ]
                    },
                    {
                        answer_text: "Outcomes",
                        sub_responses: [
                            {
                                answer_text: question3?.outcomes,
                                sub_responses: []
                            }
                        ]
                    }
                ]
            },
            question4: {
                question_title: 'What actions have been implemented?',
                responses: [
                    {
                        answer_text: question4?.actionsImplemented,
                        sub_responses: [],
                    }
                ]
            },
            question5: {
                question_title: 'What is the desired outcome?',
                responses: [
                    {
                        answer_text: question5?.intendedOutcome,
                        sub_responses: [],
                    }
                ]
            },
            question6: {
                question_title: 'What questions would you want us to ask the employee?',
                responses: []
            },
            question7: {
                question_title: 'Additional Information',
                responses: [
                    {
                        answer_text: "Additional Information",
                        responses: [],
                        text_response: question6?.additionalInfo
                    }
                ]
            }
        }

        setFormData(prevFormData => ({ ...prevFormData, form_responses: migratedFormResponses }))
    }

    const toggleEditMode = () => {
        setEditMode(prevEditMode => !prevEditMode)
    }

    const checkHoursEntered = () => {
        if (workingHoursDropdown === 'FT') {
            return true
        } else if (workingHoursDropdown === 'PT' && formData.staff_details.working_hours) {
            return true
        } else {
            return false
        }
    }

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(email)
    }

    const validatePhoneNumber = (phoneNumber) => {
        const phoneNumberUtil = PhoneNumberUtil.getInstance()

        try {
            const parsedNumber = phoneNumberUtil.parse(phoneNumber, '+44')
            const isValidNumber = phoneNumberUtil.isValidNumber(parsedNumber)
            return isValidNumber
        } catch (e) {
            return false
        }
    }

    const isStaffDetailsComplete = () => {
        const values = formData.staff_details
        const isEmailValid = values?.staff_email && validateEmail(values.staff_email)

        let isPhoneValid = false
        if (values?.phone_number) {
            let phoneNumber = values.phone_number
            if (phoneNumber.length === 11 && phoneNumber.startsWith('0')) {
                phoneNumber = phoneNumber.replace(/^0/, '')
            }
            isPhoneValid = validatePhoneNumber('+44' + phoneNumber)
        }

        return values?.first_name?.length > 0 &&
            values?.last_name?.length > 0 &&
            isEmailValid &&
            values?.date_of_birth?.length > 0 &&
            isPhoneValid &&
            values?.role?.length > 0 &&
            checkHoursEntered() &&
            (values.in_work === true || values.in_work === false)
    }

    const isPurposeComplete = () => {
        const decl = formData.declarations
        return decl.purpose_explained && decl.questions_aware && decl.abandonment
    }

    const validateNestedOptions = (responses) => {
        if (!responses || responses.length === 0) {
            return false
        }

        const optionsWithRequiredSubOptions = [
            'Policy/Attendance triggers',
            'Conduct and Performance triggers',
            'Health and Safety Triggers',
            'Functional impact on role',
            'Health related factors',
            'Personal circumstances',
            'Wellbeing concerns',
            'Mental health',
            'Neurodiversity',
            'Physical health'
        ]

        for (const response of responses) {
            if (response.answer_text?.toLowerCase().includes('other')) {
                if (!response?.text_response || response?.text_response.trim() === '') {
                    return false
                }
            }

            if (optionsWithRequiredSubOptions.includes(response.answer_text)) {
                if (!response.sub_responses || response.sub_responses.length === 0) {
                    return false
                }
            }

            if (response.has_sub_options && (!response.sub_responses || response.sub_responses.length === 0)) {
                return false
            }

            if (response.sub_responses && response.sub_responses.length > 0) {
                if (!validateNestedOptions(response.sub_responses)) {
                    return false
                }
            }
        }

        return true
    }

    const isQuestionComplete = (questionNumber) => {

        switch (questionNumber) {
            case 1:
                return isStaffDetailsComplete()
            case 2:
                if (formData?.form_responses?.[`question${questionNumber - 1}`]?.responses?.length === 0) {
                    return false
                }

                let questionResponses = formData?.form_responses?.[`question${questionNumber - 1}`]?.responses

                if (!validateNestedOptions(questionResponses)) {
                    return false
                }

                for (const response of questionResponses) {
                    if (response.answer_text === 'Policy/Attendance triggers' && response.sub_responses?.length > 0) {
                        for (const subResponse of response.sub_responses) {
                            if (subResponse.answer_text === 'Long term absence') {
                                if (!subResponse?.duration || subResponse?.duration?.trim() === '') {
                                    return false
                                }
                            }
                        }
                    }
                }

                return true
            case 3:
                if (formData?.form_responses?.[`question${questionNumber - 1}`]?.responses?.length === 0) {
                    return false
                }

                let question3Responses = formData?.form_responses?.[`question${questionNumber - 1}`]?.responses

                if (!validateNestedOptions(question3Responses)) {
                    return false
                }

                for (const response of question3Responses) {
                    if (response.answer_text === 'Health related factors' && response.sub_responses?.length > 0) {
                        for (const subResponse of response.sub_responses) {
                            if (['Mental health', 'Neurodiversity', 'Physical health'].includes(subResponse.answer_text) && subResponse.sub_responses?.length > 0) {
                                for (const subSubResponse of subResponse.sub_responses) {
                                    if (['Other mental health conditions', 'Other neurodiversity', 'Other medical issue'].includes(subSubResponse.answer_text)) {
                                        if (!subSubResponse?.text_response || subSubResponse?.text_response.trim() === '') {
                                            return false
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                return true
            case 4:
                const responses = formData?.form_responses?.question3?.responses || [];
                const meetingsParent = responses.find(r => r.answer_text === "Meetings Held");
                const outcomesParent = responses.find(r => r.answer_text === "Outcomes");
                const hasMeetingsHeld = meetingsParent && meetingsParent.sub_responses && meetingsParent.sub_responses.length > 0;
                const hasOutcomes = outcomesParent && outcomesParent.sub_responses && outcomesParent.sub_responses.length > 0;

                return (hasMeetingsHeld && hasOutcomes)
            case 5:
                if (formData?.form_responses?.[`question${questionNumber - 1}`]?.responses?.length === 0) {
                    return false
                }

                let question5Responses = formData?.form_responses?.[`question${questionNumber - 1}`]?.responses

                return validateNestedOptions(question5Responses)
            case 6:
                if (formData?.form_responses?.question5?.responses?.length === 0) {
                    return false
                }

                return validateNestedOptions(formData?.form_responses?.question5?.responses)
            case 7:
                return true
            case 8:
                return (files.fileList.length > 0 || (requestData?.supporting_documents?.length ?? 0) > 0)
            case 9:
                return isPurposeComplete()
            default:
                return false
        }

    }

    const isFormValid = () => {
        const values = formData.staff_details
        const hasStaffInfo = isStaffDetailsComplete()

        const hasDeclarations = isPurposeComplete()

        const allQuestionsComplete = isQuestionComplete(1) &&
            isQuestionComplete(2) &&
            isQuestionComplete(3) &&
            isQuestionComplete(4) &&
            isQuestionComplete(5) &&
            isQuestionComplete(6) &&
            isQuestionComplete(7) &&
            isQuestionComplete(8) &&
            isQuestionComplete(9)

        return hasStaffInfo && hasDeclarations && allQuestionsComplete
    }

    const clearFields = () => {
        setFormData({
            staff_details: {
                first_name: '',
                last_name: '',
                staff_name: '',
                staff_email: '',
                date_of_birth: '',
                phone_number: '',
                role: '',
                in_work: false,
                full_time: false,
                working_hours: 37.5,
                contact_name: '',
                contact_email: '',
            },
            form_responses: {
                question1: {
                    question_title: 'What support is being sought and why?',
                    responses: []
                },
                question2: {
                    question_title: 'What is the main nature of the employee issue?',
                    responses: []
                },
                question3: {
                    question_title: 'What has happened so far?',
                    responses: []
                },
                question4: {
                    question_title: 'What actions have been implemented?',
                    responses: []
                },
                question5: {
                    question_title: 'What is the desired outcome?',
                    responses: []
                },
                question6: {
                    question_title: 'What questions would you want us to ask the employee?',
                    responses: []
                },
                question7: {
                    question_title: 'Additional Information',
                    responses: {
                        additional_info: {
                            label: 'Additional Information',
                            value: ''
                        }
                    }
                }
            },
            declarations: {
                purpose_explained: false,
                question_aware: false,
                abandonment: false
            },
            titleId: '13172b95-d9db-431d-957c-7f3049c24f6f',
            policyId: auth?.currentPolicy?.policy_id ?? '',
            school_name: auth.schools[0].name ?? '',
            school_address: auth.schools[0].address ?? '',
            school_postcode: auth.schools[0].postcode ?? '',
            org_id: auth.schools[0].id ?? '',
            org_urn: auth.schools[0].urn ?? '',
        })

        setFiles({
            fileList: [],
            uploading: false,
            errorMsg: false,
        })
    }

    const fillMissingDevelopmentData = () => {
        const examples = [
            {
                first_name: 'Alex',
                last_name: 'Taylor',
                staff_email: 'alex.taylor@example.com',
                date_of_birth: '1988-04-12',
                phone_number: '7911123456',
                role: 'Teaching Assistant',
                in_work: true,
                question1: 'General Advice',
                question2: 'Wellbeing concerns',
                question2SubResponse: 'Request For Wellbeing Support',
                meeting: 'Welfare (Attendance) Meeting',
                outcome: 'Adjustments Agreed',
                implementation: 'Temporary Adjustments',
                desiredOutcome: 'Advice On Adjustments',
            },
            {
                first_name: 'Morgan',
                last_name: 'Reed',
                staff_email: 'morgan.reed@example.com',
                date_of_birth: '1979-09-23',
                phone_number: '7400123456',
                role: 'Classroom Teacher',
                in_work: false,
                question1: 'Conduct and Performance triggers',
                question1SubResponse: 'Performance concerns',
                question2: 'Functional impact on role',
                question2SubResponse: 'Requires Workplace Adjustments',
                meeting: 'Return To Work Meeting',
                outcome: 'Attendance Plan Agreed',
                implementation: 'Phased Return To Work',
                desiredOutcome: 'Advice On Fitness For Work',
            },
        ]
        const example = examples[Math.floor(Math.random() * examples.length)]
        const isBlank = (value) => value === undefined || value === null || (typeof value === 'string' && value.trim() === '')
        const makeResponse = (answer_text) => ({ answer_text, sub_responses: [] })

        setWorkingHoursDropdown(currentValue => currentValue || 'FT')
        setFormData(previous => {
            const staffDetails = previous.staff_details || {}
            const formResponses = previous.form_responses || {}
            const question1Responses = [...(formResponses.question1?.responses || [])]
            const question2Responses = [...(formResponses.question2?.responses || [])]
            const question3Responses = [...(formResponses.question3?.responses || [])]
            const question4Responses = [...(formResponses.question4?.responses || [])]
            const question5Responses = [...(formResponses.question5?.responses || [])]
            const question6Responses = [...(formResponses.question6?.responses || [])]
            const question7Responses = [...(formResponses.question7?.responses || [])]

            if (question1Responses.length === 0) {
                question1Responses.push({
                    ...makeResponse(example.question1),
                    sub_responses: example.question1SubResponse ? [makeResponse(example.question1SubResponse)] : [],
                })
            }

            const question1DefaultSubResponses = {
                'Policy/Attendance triggers': 'Short term absence',
                'Conduct and Performance triggers': 'Performance concerns',
                'Health and Safety Triggers': 'Health and safety concern following risk assessment',
            }
            question1Responses.forEach((response, index) => {
                const defaultSubResponse = question1DefaultSubResponses[response.answer_text]
                if (defaultSubResponse && !response.sub_responses?.length) {
                    question1Responses[index] = { ...response, sub_responses: [makeResponse(defaultSubResponse)] }
                }
                if (response.answer_text === 'Other' && isBlank(response.text_response)) {
                    question1Responses[index] = { ...question1Responses[index], text_response: 'Development test support request.' }
                }
            })

            if (question2Responses.length === 0) {
                question2Responses.push({
                    ...makeResponse(example.question2),
                    sub_responses: [makeResponse(example.question2SubResponse)],
                })
            }

            const question2DefaultSubResponses = {
                'Functional impact on role': 'Reduced Functional Capacity',
                'Health related factors': 'Mental health',
                'Personal circumstances': 'Caring Responsibilities',
                'Wellbeing concerns': 'Request For Wellbeing Support',
            }
            const healthDefaultSubResponses = {
                'Mental health': 'Anxiety/depression',
                Neurodiversity: 'ADHD',
                'Physical health': 'Musculoskeletal',
            }
            question2Responses.forEach((response, index) => {
                const defaultSubResponse = question2DefaultSubResponses[response.answer_text]
                let updatedResponse = response

                if (defaultSubResponse && !updatedResponse.sub_responses?.length) {
                    updatedResponse = { ...updatedResponse, sub_responses: [makeResponse(defaultSubResponse)] }
                }
                if (updatedResponse.answer_text === 'Other factors' && isBlank(updatedResponse.text_response)) {
                    updatedResponse = { ...updatedResponse, text_response: 'Development test factor.' }
                }

                if (updatedResponse.answer_text === 'Health related factors') {
                    updatedResponse = {
                        ...updatedResponse,
                        sub_responses: (updatedResponse.sub_responses || []).map(healthResponse => {
                            const defaultHealthSubResponse = healthDefaultSubResponses[healthResponse.answer_text]
                            let updatedHealthResponse = healthResponse

                            if (defaultHealthSubResponse && !updatedHealthResponse.sub_responses?.length) {
                                updatedHealthResponse = { ...updatedHealthResponse, sub_responses: [makeResponse(defaultHealthSubResponse)] }
                            }

                            updatedHealthResponse = {
                                ...updatedHealthResponse,
                                sub_responses: (updatedHealthResponse.sub_responses || []).map(detailResponse => (
                                    ['Other mental health conditions', 'Other neurodiversity', 'Other medical issue'].includes(detailResponse.answer_text) && isBlank(detailResponse.text_response)
                                        ? { ...detailResponse, text_response: 'Development test detail.' }
                                        : detailResponse
                                )),
                            }

                            return updatedHealthResponse
                        }),
                    }
                }

                question2Responses[index] = updatedResponse
            })

            const ensurePreviousAction = (parentAnswer, responseAnswer) => {
                const parentIndex = question3Responses.findIndex(response => response.answer_text === parentAnswer)
                if (parentIndex === -1) {
                    question3Responses.push({ answer_text: parentAnswer, sub_responses: [makeResponse(responseAnswer)] })
                    return
                }

                if (!question3Responses[parentIndex].sub_responses?.length) {
                    question3Responses[parentIndex] = {
                        ...question3Responses[parentIndex],
                        sub_responses: [makeResponse(responseAnswer)],
                    }
                }
            }

            ensurePreviousAction('Meetings Held', example.meeting)
            ensurePreviousAction('Outcomes', example.outcome)

            if (question4Responses.length === 0) question4Responses.push(makeResponse(example.implementation))
            question4Responses.forEach((response, index) => {
                if (response.answer_text === 'Other' && isBlank(response.text_response)) {
                    question4Responses[index] = { ...response, text_response: 'Development test implementation.' }
                }
            })
            if (question5Responses.length === 0) question5Responses.push(makeResponse(example.desiredOutcome))
            if (question6Responses.length === 0) {
                question6Responses.push(
                    makeResponse('Is the employee fit for their normal hours and duties required by their post? If not, when will they be able to return to work/return to normal hours/duties or alternative work?'),
                    makeResponse('What if any work adjustments may facilitate a return to the substantive role?'),
                )
            }

            if (question7Responses.length === 0) {
                question7Responses.push({
                    answer_text: 'Additional Information',
                    responses: [],
                    text_response: 'Development example referral for testing purposes.',
                })
            } else if (isBlank(question7Responses[0].text_response)) {
                question7Responses[0] = {
                    ...question7Responses[0],
                    text_response: 'Development example referral for testing purposes.',
                }
            }

            return {
                ...previous,
                staff_details: {
                    ...staffDetails,
                    first_name: isBlank(staffDetails.first_name) ? example.first_name : staffDetails.first_name,
                    last_name: isBlank(staffDetails.last_name) ? example.last_name : staffDetails.last_name,
                    staff_email: isBlank(staffDetails.staff_email) ? example.staff_email : staffDetails.staff_email,
                    date_of_birth: isBlank(staffDetails.date_of_birth) ? example.date_of_birth : staffDetails.date_of_birth,
                    phone_number: isBlank(staffDetails.phone_number) ? example.phone_number : staffDetails.phone_number,
                    role: isBlank(staffDetails.role) ? example.role : staffDetails.role,
                    in_work: staffDetails.in_work === null || staffDetails.in_work === undefined ? example.in_work : staffDetails.in_work,
                },
                form_responses: {
                    ...formResponses,
                    question1: { ...formResponses.question1, responses: question1Responses },
                    question2: { ...formResponses.question2, responses: question2Responses },
                    question3: { ...formResponses.question3, responses: question3Responses },
                    question4: { ...formResponses.question4, responses: question4Responses },
                    question5: { ...formResponses.question5, responses: question5Responses },
                    question6: { ...formResponses.question6, responses: question6Responses },
                    question7: { ...formResponses.question7, responses: question7Responses },
                },
                declarations: {
                    ...previous.declarations,
                    purpose_explained: true,
                    questions_aware: true,
                    abandonment: true,
                },
            }
        })
    }

    const buildRequestPayload = () => {

        let payload = formData;

        let formattedPhoneNumber = formData.staff_details?.phone_number || ''

        if (formattedPhoneNumber) {
            if (formattedPhoneNumber.length === 11 && formattedPhoneNumber.startsWith('0')) {
                formattedPhoneNumber = formattedPhoneNumber.replace(/^0/, '')
            }

            formattedPhoneNumber = formattedPhoneNumber.replace(/\+44/g, '')
            formattedPhoneNumber = '+44' + formattedPhoneNumber
        }

        payload.staff_details.phone_number = formattedPhoneNumber
        payload.staff_details.staff_name = `${formData.staff_details.first_name} ${formData.staff_details.last_name}`

        payload.staff_details.working_hours = workingHoursDropdown === 'PT' ? parseFloat(formData.staff_details.working_hours) : 37.5

        return payload
    }

    const uploadDocuments = async (serviceRequestId) => {
        if (files.fileList.length === 0) {
            createInfo('error', 'Please select files to upload');
            return;
        }

        const data = new FormData();

        for (let i = 0; i < files.fileList.length; i++) {
            data.append('file[]', files.fileList[i], files.fileList[i].name);
        }

        if (auth?.user?.id) {
            data.append('userId', auth?.user?.contact_id);
        }
        if (auth?.user?.name) {
            data.append('userName', auth?.user?.contact_name);
        }

        try {
            const response = await axios.post(`/upload-supporting-document/${serviceRequestId}`, data,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            if (response.status !== 200) {
                createInfo('error', 'Failed to upload documents');
                return;
            }

            setFiles({
                fileList: [],
                uploading: false,
                errorMsg: false,
            });
            createInfo('success', 'Document Successfully Uploaded');
        } catch (error) {
            console.error('File upload error:', error);
            createInfo('error', 'Failed to upload documents');
        }
    }

    const submitRequest = async (e) => {
        e.preventDefault()
        setShowLoader(true)

        try {
            const payload = buildRequestPayload()

            if (isDraftEdit || isEdit) {
                const updateResponse = await axios.post(`/service-requests/oh/update/${requestData.id}`, payload)

                if (updateResponse.status !== 200) {
                    console.error('Failed to update draft before submitting')
                    createInfo('error', 'Failed to submit referral, please try again')
                    return
                }

                if (files.fileList.length > 0) {
                    try {
                        await uploadDocuments(requestData.id)
                    } catch (error) {
                        console.error('Failed to upload supporting documents')
                        createInfo('error', 'Referral updated but failed to upload supporting documents')
                    }
                }

                if (isDraftEdit) {
                    const submitResponse = await axios.post(`/service-requests/oh/submit/${requestData.id}`)

                    if (submitResponse.status !== 200) {
                        console.error('Failed to submit draft')
                        createInfo('error', 'Failed to submit referral, please try again')
                        return
                    }
                }

                clearFields()
                createInfo('success', 'Referral submitted successfully')
                navigate('/oh/dashboard')
                return
            }

            const response = await axios.post('/service-requests/oh/create', payload)

            if (response.status !== 200) {
                console.error('Failed to submit request')
                createInfo('error', 'Failed to submit request, please try again')
                return
            }

            let service_request = response.data.service_request

            if (files.fileList.length > 0) {
                try {
                    await uploadDocuments(service_request.id)
                } catch (error) {
                    console.error('Failed to upload supporting documents')
                    createInfo('error', 'Request created but failed to upload supporting documents')
                }
            }

            clearFields()
            createInfo('success', 'OH Request created successfully')
            navigate('/oh/dashboard')
        } catch (error) {
            console.error('Error:', error)
            if (error?.response?.status === 403) {
                createInfo('error', 'Editing This Request is No Longer Allowed')
            }
            createInfo('error', 'Failed to submit request, please try again')
        } finally {
            setShowLoader(false)
        }
    }

    const requestSaveForReview = (e) => {
        e.preventDefault()
        setSaveDraftConfirmOpen(true)
    }

    const saveForReview = async () => {
        setSaveDraftConfirmOpen(false)
        setShowLoader(true)

        try {
            const basePayload = {
                ...buildRequestPayload(),
                draft: true,
            }

            const response = isDraftEdit
                ? await axios.post(`/service-requests/oh/update/${requestData.id}`, basePayload)
                : await axios.post('/service-requests/oh/create', basePayload)

            if (response.status !== 200) {
                console.error('Failed to save draft')
                createInfo('error', 'Failed to save draft, please try again')
                return
            }

            let service_request = response.data.service_request
            const serviceRequestId = isDraftEdit ? requestData.id : service_request.id

            if (files.fileList.length > 0) {
                try {
                    await uploadDocuments(serviceRequestId)
                } catch (error) {
                    console.error('Failed to upload supporting documents')
                    createInfo('error', 'Draft saved but failed to upload supporting documents')
                }
            }

            clearFields()
            createInfo('success', 'Draft saved for review')
            navigate('/oh/dashboard')
        } catch (error) {
            console.error('Error:', error)
            createInfo('error', 'Failed to save draft, please try again')
        } finally {
            setShowLoader(false)
        }
    }

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#02ADAD]"></div>
            </div>
        )
    }

    if (!orgInfo) {
        return (
            <div className="text-center py-8">
                <p className="text-red-600">Error: Unable to load organization information</p>
                <button
                    onClick={() => navigate('/oh/dashboard')}
                    className="mt-4 px-4 py-2 bg-[#02ADAD] text-white rounded hover:bg-[#018f8f]"
                >
                    Return to Dashboard
                </button>
            </div>
        )
    }

    return (
        <div className="w-full max-w-4xl mx-auto bg-white rounded-lg">
            <div className="mb-4 flex items-center justify-between gap-4 p-4 border-b border-gray-200">
                <button
                    onClick={() => navigate('/oh/dashboard')}
                    className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Dashboard
                </button>
                {process.env.REACT_APP_ENV !== 'production' && (
                    <button
                        type="button"
                        onClick={fillMissingDevelopmentData}
                        className="shrink-0 rounded-md bg-amber-100 px-4 py-2 text-sm font-medium text-amber-900 transition-colors hover:bg-amber-200"
                    >
                        Fill missing test data
                    </button>
                )}
            </div>

            <form ref={formRef} onSubmit={submitRequest}>
                <div className="relative flex-1 pb-16">
                    <div className="px-4 py-2">
                        <OHRequestForm
                            requestData={requestData}
                            setRequestData={setRequestData}
                            formData={formData}
                            setFormData={setFormData}
                            files={files}
                            setFiles={setFiles}
                            workingHoursDropdown={workingHoursDropdown}
                            setWorkingHoursDropdown={setWorkingHoursDropdown}
                            isQuestionComplete={isQuestionComplete}
                        />
                    </div>
                </div>

                <div className="bottom-0 z-20 bg-white border-t border-gray-200">
                    <UserDetailsSection
                        auth={auth}
                        orgInfo={orgInfo}
                        formData={formData}
                        setFormData={setFormData}
                        editMode={editMode}
                        toggleEditMode={toggleEditMode}
                        showMoreBelowHint={showMoreBelowHint}
                    />

                    <div className="px-4 pb-3">
                        <div className="w-full mx-auto relative">
                            {showValidationTooltip && (
                                <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-red-600 text-white text-sm px-3 py-2 rounded-md shadow-lg z-10 whitespace-nowrap">
                                    <div className="text-center">Please complete all required fields</div>
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-red-600"></div>
                                </div>
                            )}
                            {isFormValid() && (!requestData || requestData?.request_status?.toLowerCase() === "draft") && (
                                <button
                                    type="submit"
                                    className={`border flex items-center rounded h-[42px] w-full transition-shadow ${draftOnly
                                        ? 'border-amber-200 bg-amber-50 cursor-not-allowed'
                                        : 'border-[#02ADAD] cursor-pointer hover:shadow-md'
                                        }`}
                                    onClick={submitRequest}
                                    disabled={draftOnly}
                                >
                                    <div className={`h-full flex justify-center items-center px-6 font-medium w-full ${draftOnly ? 'text-sm text-amber-800' : 'text-[#02ADAD]'}`}>
                                        {draftOnly ? 'You do not have the ability to submit this request' : (isDraftEdit ? 'Submit referral' : 'Submit OH Request')}
                                    </div>
                                    <div className={`h-full flex w-[42px] justify-center items-center ${draftOnly ? 'border-l border-amber-200 bg-amber-100 text-amber-600' : 'bg-[#02ADAD] text-white'}`}>
                                        <FontAwesomeIcon icon={requestData ? faEdit : faPlus} />
                                    </div>
                                </button>
                            )}
                            {requestData && requestData?.request_status?.toLowerCase() !== "draft" && (
                                <button
                                    type="submit"
                                    className={`border mt-3 flex items-center rounded h-[42px] w-full transition-shadow ${draftOnly
                                        ? 'border-amber-200 bg-amber-50 cursor-not-allowed'
                                        : `border-[#02ADAD] cursor-pointer hover:shadow-md ${!isFormValid() ? 'opacity-50 disabled:cursor-not-allowed bg-gray-200' : ''}`
                                        }`}
                                    onClick={() => submitRequest}
                                    disabled={!isFormValid() || draftOnly}
                                >
                                    <div className={`h-full flex justify-center items-center px-6 font-medium w-full ${draftOnly ? 'text-sm text-amber-800' : 'text-[#02ADAD]'}`}>
                                        {draftOnly ? 'Only Master OH users can submit referrals' : 'Update OH Request'}
                                    </div>
                                    <div className={`h-full flex w-[42px] justify-center items-center ${draftOnly ? 'border-l border-amber-200 bg-amber-100 text-amber-600' : 'bg-[#02ADAD] text-white'}`}>
                                        <FontAwesomeIcon icon={faEdit} />
                                    </div>
                                </button>
                            )}
                            {(!requestData || requestData?.request_status?.toLowerCase() === "draft") &&
                                <>
                                    <p className="mt-3 mb-1 text-center text-gray-400 text-sm">Don't want to submit just yet?</p>

                                    <button
                                        type="button"
                                        className="border flex items-center rounded h-[42px] w-full border-gray-700 cursor-pointer hover:shadow-md transition-shadow"
                                        onClick={requestSaveForReview}
                                    >
                                        <div className="h-full flex justify-center items-center px-6 font-medium w-full text-gray-800">
                                            {isDraftEdit ? 'Save changes (Draft — does not submit)' : 'Save for review (Draft — does not submit)'}
                                        </div>
                                        <div className="text-white h-full flex w-[42px] justify-center items-center bg-gray-700">
                                            <FontAwesomeIcon icon={faEdit} />
                                        </div>
                                    </button>
                                </>
                            }

                        </div>
                    </div>
                </div>
            </form >

            <CancelRequest
                isOpen={saveDraftConfirmOpen}
                onClose={() => setSaveDraftConfirmOpen(false)}
                onCancel={saveForReview}
                title={isDraftEdit ? 'Save changes as draft?' : 'Save for review?'}
                message="Saving for review will NOT submit this referral. It will be saved as a draft which you can submit later. Do you want to continue?"
                confirmLabel={isDraftEdit ? 'Save Draft Changes' : 'Save as Draft'}
                confirmButtonClassName="inline-flex w-full justify-center rounded-md bg-[#02ADAD] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#02ADAD] sm:col-start-2"
            />
        </div>
    )
}

export default OHRequestPage
